/**
 * Status effects (vanilla / Bedrock numbers). DOM-free and serializable, shared by
 * the player (PlayerStats owns a set) and mobs (Mob.effects, lazily created).
 *
 *   EffectSet.add(id, amp, ticks)      apply / refresh (higher amp or longer duration wins)
 *   EffectSet.tick(target)             one 20 Hz tick: periodic damage / healing, countdown, expiry
 *   applyInstant(id, amp, target, k)   Instant Health / Damage (k = splash distance scale, undead inverted)
 *   speedMult(set) / attackBonus(set) / mineMult(set) / damageTakenMult(set, kind) ...   pure queries
 *   serialize() / EffectSet.from()     flat [id, amp, ticks, ambient]*
 *
 * Amplifier is 0-based (0 = level I). Durations are ticks.
 */

export const FX = {
  SPEED: 1, SLOWNESS: 2, HASTE: 3, MINING_FATIGUE: 4, STRENGTH: 5, INSTANT_HEALTH: 6, INSTANT_DAMAGE: 7,
  JUMP_BOOST: 8, NAUSEA: 9, REGENERATION: 10, RESISTANCE: 11, FIRE_RESISTANCE: 12, WATER_BREATHING: 13,
  INVISIBILITY: 14, BLINDNESS: 15, NIGHT_VISION: 16, HUNGER: 17, WEAKNESS: 18, POISON: 19, WITHER: 20,
  ABSORPTION: 22, LEVITATION: 25, SLOW_FALLING: 27, BAD_OMEN: 31, DARKNESS: 33,
} as const;

export interface EffectDef {
  id: number;
  name: string;
  /** Particle / icon colour (vanilla potion colours). */
  color: number;
  beneficial: boolean;
  instant?: boolean;
}

const D = (id: number, name: string, color: number, beneficial: boolean, instant = false): EffectDef =>
  ({ id, name, color, beneficial, ...(instant ? { instant: true } : {}) });

export const EFFECTS: readonly EffectDef[] = [
  D(FX.SPEED, 'Speed', 0x33ebff, true), D(FX.SLOWNESS, 'Slowness', 0x8bafe0, false),
  D(FX.HASTE, 'Haste', 0xd9c043, true), D(FX.MINING_FATIGUE, 'Mining Fatigue', 0x4a4217, false),
  D(FX.STRENGTH, 'Strength', 0xffc700, true), D(FX.INSTANT_HEALTH, 'Instant Health', 0xf82423, true, true),
  D(FX.INSTANT_DAMAGE, 'Instant Damage', 0xa9656a, false, true), D(FX.JUMP_BOOST, 'Jump Boost', 0xfdff84, true),
  D(FX.REGENERATION, 'Regeneration', 0xcd5cab, true), D(FX.RESISTANCE, 'Resistance', 0x99453a, true),
  D(FX.FIRE_RESISTANCE, 'Fire Resistance', 0xff9900, true), D(FX.WATER_BREATHING, 'Water Breathing', 0x98dac0, true),
  D(FX.INVISIBILITY, 'Invisibility', 0xf6f6f6, true), D(FX.NIGHT_VISION, 'Night Vision', 0xc2ff66, true),
  D(FX.HUNGER, 'Hunger', 0x587653, false), D(FX.WEAKNESS, 'Weakness', 0x484d48, false),
  D(FX.POISON, 'Poison', 0x87a363, false), D(FX.WITHER, 'Wither', 0x352a27, false),
  D(FX.ABSORPTION, 'Absorption', 0x2552a5, true), D(FX.SLOW_FALLING, 'Slow Falling', 0xf3cfb9, true),
  D(FX.BAD_OMEN, 'Bad Omen', 0x0b6138, false),
  D(FX.DARKNESS, 'Darkness', 0x292721, false),
  D(FX.LEVITATION, 'Levitation', 0xceffff, false),
  D(FX.NAUSEA, 'Nausea', 0x551d4a, false), D(FX.BLINDNESS, 'Blindness', 0x1f1f23, false),
];

const BY_ID = new Map<number, EffectDef>(EFFECTS.map((e) => [e.id, e]));
export const effectDef = (id: number): EffectDef | undefined => BY_ID.get(id);

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
/** "Speed II" (level I carries no numeral in the HUD list, like vanilla's translation keys). */
export function effectLabel(id: number, amp: number): string {
  const n = effectDef(id)?.name ?? 'Unknown';
  return amp <= 0 ? n : `${n} ${ROMAN[amp] ?? amp + 1}`;
}

/** m:ss like the vanilla effect timer; "**" for effectively infinite. */
export function formatDuration(ticks: number): string {
  if (ticks >= 72000) return '**';
  const s = Math.max(0, Math.floor(ticks / 20));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export interface ActiveEffect {
  id: number;
  amp: number;
  /** Ticks left. */
  dur: number;
  ambient?: boolean;
}

/** Damage kinds the effects deal; PlayerStats maps both onto its 'magic' kind. */
export type EffectDamage = 'magic' | 'wither';

/** What an effect tick needs from its owner. */
export interface EffectTarget {
  readonly health: number;
  readonly maxHealth: number;
  /** Undead: Instant Health hurts, Instant Damage heals, immune to Poison / Regeneration. */
  readonly undead?: boolean;
  heal(n: number): void;
  /** Deal `n` damage; the owner decides what is lethal. */
  hurt(n: number, kind: EffectDamage): void;
  /** Hunger effect: add exhaustion (players only). */
  exhaust?(n: number): void;
}

/** Vanilla `shouldApplyEffectTickThisTick`: Regeneration 50 >> amp, Poison 25 >> amp, Wither 40 >> amp. */
export function tickInterval(id: number, amp: number): number {
  const base = id === FX.REGENERATION ? 50 : id === FX.POISON ? 25 : id === FX.WITHER ? 40 : 0;
  return base > 0 ? base >> Math.min(amp, 30) : 0;
}

/** Does `id` act on the tick where `remaining` ticks are left? */
export function actsThisTick(id: number, amp: number, remaining: number): boolean {
  const k = tickInterval(id, amp);
  if (k > 0) return remaining % k === 0;
  return id === FX.HUNGER; // continuous effects (speed...) are queried, not ticked
}

/** Instant Health heals 4 << amp, Instant Damage hurts 6 << amp (scaled by splash distance). */
export function instantAmount(id: number, amp: number, scale = 1): number {
  const raw = id === FX.INSTANT_HEALTH ? 4 << amp : id === FX.INSTANT_DAMAGE ? 6 << amp : 0;
  return Math.floor(scale * raw + 0.5);
}

export function applyInstant(id: number, amp: number, t: EffectTarget, scale = 1): void {
  const n = instantAmount(id, amp, scale);
  if (n <= 0) return;
  const heals = (id === FX.INSTANT_HEALTH) !== !!t.undead;
  if (heals) t.heal(n); else t.hurt(n, 'magic');
}

export class EffectSet {
  readonly list: ActiveEffect[] = [];
  /** Fired when an effect is gained / refreshed (`added`) or removed. */
  listener: ((e: ActiveEffect, added: boolean) => void) | null = null;

  get size(): number { return this.list.length; }
  get(id: number): ActiveEffect | undefined { return this.list.find((e) => e.id === id); }
  has(id: number): boolean { return this.get(id) !== undefined; }
  /** Amplifier of `id`, or -1 when not active. */
  amp(id: number): number { return this.get(id)?.amp ?? -1; }

  /** Apply an effect. Returns false when a stronger / longer one is already running. */
  add(id: number, amp: number, dur: number, ambient = false): boolean {
    if (!BY_ID.has(id) || dur <= 0) return false;
    amp = Math.max(0, Math.min(255, Math.floor(amp)));
    const cur = this.get(id);
    if (cur) {
      if (amp < cur.amp || (amp === cur.amp && dur <= cur.dur)) return false;
      cur.amp = amp; cur.dur = dur; cur.ambient = ambient || undefined;
      this.listener?.(cur, true);
      return true;
    }
    const e: ActiveEffect = { id, amp, dur, ...(ambient ? { ambient } : {}) };
    this.list.push(e);
    this.listener?.(e, true);
    return true;
  }

  remove(id: number): boolean {
    const i = this.list.findIndex((e) => e.id === id);
    if (i < 0) return false;
    const [e] = this.list.splice(i, 1);
    this.listener?.(e, false);
    return true;
  }

  clear(): void {
    for (const e of this.list.splice(0)) this.listener?.(e, false);
  }

  /** Remove every harmful effect (e.g. Honey Bottle only clears Poison via `remove`). */
  clearHarmful(): void {
    for (const e of [...this.list]) if (!BY_ID.get(e.id)?.beneficial) this.remove(e.id);
  }

  /** One 20 Hz tick: periodic effects act (using the pre-decrement duration), then durations count down. */
  tick(t: EffectTarget): void {
    for (let i = this.list.length - 1; i >= 0; i--) {
      const e = this.list[i];
      if (actsThisTick(e.id, e.amp, e.dur)) applyPeriodic(e, t);
      if (--e.dur <= 0) { this.list.splice(i, 1); this.listener?.(e, false); }
    }
  }

  serialize(): number[] {
    const out: number[] = [];
    for (const e of this.list) out.push(e.id, e.amp, e.dur, e.ambient ? 1 : 0);
    return out;
  }

  load(flat: readonly number[] | undefined | null): void {
    this.list.length = 0;
    if (!Array.isArray(flat)) return;
    for (let i = 0; i + 3 < flat.length; i += 4) {
      const id = flat[i] | 0, amp = flat[i + 1] | 0, dur = flat[i + 2] | 0;
      if (BY_ID.has(id) && dur > 0 && !this.has(id)) this.list.push({ id, amp: Math.max(0, amp), dur, ...(flat[i + 3] ? { ambient: true } : {}) });
    }
  }

  static from(flat: readonly number[] | undefined | null): EffectSet {
    const s = new EffectSet();
    s.load(flat);
    return s;
  }
}

function applyPeriodic(e: ActiveEffect, t: EffectTarget): void {
  switch (e.id) {
    case FX.REGENERATION:
      if (!t.undead && t.health < t.maxHealth) t.heal(1);
      break;
    case FX.POISON:
      // never lethal to a player; the target's hurt() enforces its own floor, this keeps the 1 HP rule for everyone
      if (!t.undead && t.health > 1) t.hurt(Math.min(1, t.health - 1), 'magic');
      break;
    case FX.WITHER:
      t.hurt(1, 'wither');
      break;
    case FX.HUNGER:
      t.exhaust?.(0.005 * (e.amp + 1));
      break;
    default: break;
  }
}

// --- pure queries --------------------------------------------------------------

const amp1 = (s: EffectSet, id: number): number => s.amp(id) + 1; // 0 when absent, else the level

/** Movement speed multiplier: Speed +20% / level, Slowness -15% / level (floored at 0). */
export function speedMult(s: EffectSet): number {
  if (s.size === 0) return 1;
  return Math.max(0, (1 + 0.2 * amp1(s, FX.SPEED)) * (1 - 0.15 * amp1(s, FX.SLOWNESS)));
}

/** Flat melee damage bonus: Strength +3 / level, Weakness -4 / level. */
export function attackBonus(s: EffectSet): number {
  if (s.size === 0) return 0;
  return 3 * amp1(s, FX.STRENGTH) - 4 * amp1(s, FX.WEAKNESS);
}

/** Block-breaking speed multiplier: Haste +20% / level, Mining Fatigue 0.3^level. */
export function mineMult(s: EffectSet): number {
  if (s.size === 0) return 1;
  let m = 1 + 0.2 * amp1(s, FX.HASTE);
  const mf = amp1(s, FX.MINING_FATIGUE);
  if (mf > 0) m *= [1, 0.3, 0.09, 0.0027, 0.00081][Math.min(mf, 4)];
  return m;
}

/** Damage multiplier from Resistance (-20% / level, 5 = immune); void and starvation ignore it. */
export function damageTakenMult(s: EffectSet, kind: string): number {
  if (s.size === 0 || kind === 'void' || kind === 'starve' || kind === 'sonic_boom') return 1;
  return Math.max(0, 1 - 0.2 * amp1(s, FX.RESISTANCE));
}

/** Extra jump velocity (blocks / tick): +0.1 per Jump Boost level. */
export function jumpBoost(s: EffectSet): number { return s.size === 0 ? 0 : 0.1 * amp1(s, FX.JUMP_BOOST); }
export function fireImmune(s: EffectSet): boolean { return s.has(FX.FIRE_RESISTANCE); }
export function waterBreathing(s: EffectSet): boolean { return s.has(FX.WATER_BREATHING); }
export function slowFalling(s: EffectSet): boolean { return s.has(FX.SLOW_FALLING); }
export function invisible(s: EffectSet): boolean { return s.has(FX.INVISIBILITY); }
/**
 * Mob detection-range multiplier (vanilla LivingEntity.getVisibilityPercent): 1, or while invisible
 * 0.7 x (worn armour pieces / 4) with that fraction floored at 0.1 - so 0.07 naked, up to 0.7 fully armoured.
 */
export function visibilityScale(s: EffectSet, armorPieces: number): number {
  if (!invisible(s)) return 1;
  const cover = Math.max(0.1, Math.min(1, armorPieces / 4));
  return 0.7 * cover;
}
/** Night Vision strength 0..1; flickers over the last 10 s like vanilla. */
export function nightVision(s: EffectSet, tick = 0): number {
  const e = s.get(FX.NIGHT_VISION);
  if (!e) return 0;
  return e.dur > 200 ? 1 : 0.7 + 0.3 * Math.sin((e.dur - tick) * Math.PI * 0.2);
}
/**
 * Darkness strength 0..1 for the view dimming: pulses (vanilla ~ every 2.5 s) and eases in/out over the first/last second.
 * Rendered by the HUD overlay in ui/effectsHud.ts.
 */
export function darkness(s: EffectSet, tick = 0): number {
  const e = s.get(FX.DARKNESS);
  if (!e) return 0;
  const pulse = 0.5 + 0.5 * Math.sin(tick * (Math.PI / 25));
  return (0.35 + 0.65 * pulse) * Math.min(1, e.dur / 20);
}
/** Blindness strength 0..1 for the view dimming (vanilla fades the fog in over the first second and out over the last). */
export function blindness(s: EffectSet): number {
  const e = s.get(FX.BLINDNESS);
  return e ? Math.min(1, e.dur / 20) : 0;
}
/** Nausea strength 0..1 for the wobbling view overlay (eases out over the last second). */
export function nausea(s: EffectSet): number {
  const e = s.get(FX.NAUSEA);
  return e ? Math.min(1, e.dur / 20) : 0;
}
/** Absorption hearts' capacity in health points (4 per level). */
export function absorptionCap(s: EffectSet): number { return 4 * amp1(s, FX.ABSORPTION); }

/** Mob types the Instant effects invert on (and that ignore Poison / Regeneration). */
const UNDEAD = new Set(['zombie', 'skeleton', 'wither_skeleton', 'zombified_piglin', 'zombie_villager', 'zombie_horse', 'husk', 'stray', 'drowned', 'phantom', 'wither']);
export const isUndeadType = (type: string): boolean => UNDEAD.has(type);

/** Structural slice of Mob used here, so this module never imports the entity code. */
export interface MobEffectTarget {
  health: number;
  readonly maxHealth: number;
  readonly type: string;
  readonly alive: boolean;
  effects: EffectSet | null;
  hurt(amount: number): boolean;
}

/** EffectTarget view of a mob (poison and wither can kill mobs; players survive poison at 1 HP). */
export function mobTarget(m: MobEffectTarget): EffectTarget {
  return {
    get health() { return m.health; }, maxHealth: m.maxHealth, undead: isUndeadType(m.type),
    heal: (n) => { m.health = Math.min(m.maxHealth, m.health + n); },
    // magic damage bypasses the i-frame window the way vanilla's does at these intervals
    hurt: (n) => { m.hurt(n); },
  };
}

/** Per-tick mob effects; call from Mob.tick while alive. */
export function tickMobEffects(m: MobEffectTarget): void {
  const s = m.effects;
  if (!s) return;
  if (s.size === 0) { m.effects = null; return; }
  s.tick(mobTarget(m));
}

/** Apply `id` to a mob (Instant effects act immediately and are never stored). */
export function giveMobEffect(m: MobEffectTarget, id: number, amp: number, dur: number, scale = 1): void {
  if (!m.alive) return;
  if (effectDef(id)?.instant) { applyInstant(id, amp, mobTarget(m), scale); return; }
  if (isUndeadType(m.type) && (id === FX.POISON || id === FX.REGENERATION)) return;
  (m.effects ??= new EffectSet()).add(id, amp, Math.max(1, Math.floor(dur * scale + 0.5)));
}
